import { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE } from "../document/types.ts";
import { COMPILED_MIN_COVER, footprintBlocks, type PropFootprint } from "../compile/footprint.ts";
import { footprintFromCells } from "../compile/footprint-override.ts";

/**
 * The cells a character is stopped by around a frame in the footprint panel (ADR-113 decisions 2 and 14, ADR-131
 * decision 7). The game blocks on the compiled grid, where a footprint is stamped at the compiled cell with the
 * compiled cell's cover rule, not on the authoring cells the panel draws, so a figure that is to stop exactly where
 * the game stops a character is held by these cells and not by the drawn ones.
 */

/**
 * Pixels in one compiled cell for a panel whose authoring cell is `cellPixels` wide.
 *
 * @param cellPixels Pixels per authoring cell.
 * @returns Pixels per compiled cell.
 */
export function compiledCellPixels(cellPixels: number): number {
  return cellPixels * COLLISION_CELLS_PER_TILE / COMPILED_COLLISION_CELLS_PER_TILE;
}

/**
 * The compiled cells a frame blocks, laid from the frame's bottom-left corner as `footprintBlocks` lays a footprint:
 * those of its footprint when it has one, else those of the cells drawn at the panel's cell, stamped as a compile
 * stamps cells an author painted (`footprintFromCells`).
 *
 * @param args.footprint The frame's footprint as the compile will read it, or undefined when the cells drawn are
 *   what the compile reads.
 * @param args.cells The authoring cells drawn, as `column,rowsUp` keys.
 * @param args.size The frame's size in pixels.

 * @param args.cellPixels Pixels per cell of the cells drawn.
 * @param args.stampPixels Pixels per cell of the cells returned: the compiled cell. Half of `cellPixels` when not given,
 *   as for cells drawn at the authoring cell; a panel that draws at the compiled cell gives it.
 * @returns The cells that block, as `column,rowsUp` keys in cells of `stampPixels`.
 */
export function figureStampCells(args: { readonly footprint: PropFootprint | undefined; readonly cells: ReadonlySet<string>; readonly size: { readonly w: number; readonly h: number }; readonly cellPixels: number; readonly stampPixels?: number }): ReadonlySet<string> {
  const { footprint, cells, size, cellPixels } = args;
  const stampPixels = args.stampPixels ?? compiledCellPixels(cellPixels);
  const read: PropFootprint =
    footprint ??
    footprintFromCells(
      [...cells].map((key) => key.split(",").map(Number) as [number, number]),
      size.w,
      size.h,
      cellPixels,
    );
  return new Set(footprintBlocks(read, stampPixels, false, COMPILED_MIN_COVER).map(([x, y]) => `${x},${y}`));
}
